import type { ApiResponse, TierFeature } from '@bower/shared';
import type { AiQuotaState, HttpClient } from './http-client.types';

export interface CreatePortalSessionBody {
  flowType?: 'switch-plan' | 'manage' | 'reactivate' | 'payment-method-update';
  tier?: 'PRO' | 'TEAM';
  billingCycle?: 'monthly' | 'annual';
  confirmBillingOwnership?: boolean;
}

export function getAiQuotaUsage(http: HttpClient) {
  return http.get<{
    chat: AiQuotaState;
    liveMinutes: AiQuotaState;
    textExtractions: AiQuotaState;
    transcriptionMinutes: AiQuotaState;
    codeExecutionMinutes: AiQuotaState;
  }>('/quota/me');
}

export function getWorkspacePlan(http: HttpClient) {
  return http.get<{
    tier: string;
    status: string;
    hasStripeSubscription: boolean;
    billingCycle: string | null;
    currentPeriodEnd: string | null;
    cancelAtPeriodEnd: boolean;
    cancelAt: string | null;
    seats: {
      paidSeatCount: number;
      paidMemberCount: number;
      guestCount: number;
      pendingPaidInvitations: number;
      pendingGuestInvitations: number;
      maxSeats: number | null;
      maxGuests: number | null;
    };
    limits: {
      storageBytes: number;
      aiQuotas: Record<
        | 'chat'
        | 'liveMinutes'
        | 'textExtractions'
        | 'transcriptionMinutes'
        | 'codeExecutionMinutes',
        number | null
      >;
      maxMembers: number;
      storageQuotaBytes: number;
      maxWorkspaces: number | null;
    };
    usage: {
      storageUsedBytes: number;
      ownedWorkspaceCount: number | null;
      memberCount: number;
    };
    features: Record<TierFeature, boolean>;
    trialPaymentMethodMissing: boolean;
  }>('/billing/plan');
}

export function redeemPromotion(http: HttpClient, code: string) {
  return http.post<{ fullyDiscounted: boolean }>('/billing/promotions/redeem', { code });
}

export function previewAddSeat(http: HttpClient) {
  return http.post<{
    proratedAmountCents: number;
    amountDueTodayCents: number;
    /** Per-period total once the seat is live. Null when Stripe has no
     *  `unit_amount` for the price, in which case the UI omits the figure. */
    recurringAmountCents: number | null;
    currency: string;
    periodEnd: string | null;
    requiresPaymentMethod: boolean;
    /** Why a payment method is required. `cap-reached` means the card-free
     *  trial allowance is genuinely spent; null when no payment method is
     *  required. Only `cap-reached` may claim the cap. */
    paymentMethodReason: 'cap-reached' | null;
    isTrial: boolean;
    trialEnd: string | null;
    trialSeatsRemaining: number | null;
  }>('/billing/seats/preview', {});
}

export function purchaseSeatAndInvite(
  http: HttpClient,
  email: string,
  intendedRole: 'ADMIN' | 'MEMBER',
) {
  return http.post<{
    invitationId: string;
    invitationToken: string;
    proratedAmountCents: number;
    currency: string;
  }>('/billing/seats/purchase', { email, intendedRole });
}

export function inviteGuest(http: HttpClient, email: string) {
  return http.post<{ invitationId: string; invitationToken: string }>(
    '/billing/seats/guest-invite',
    { email },
  );
}

export function inviteMemberDirect(
  http: HttpClient,
  email: string,
  intendedRole: 'MEMBER' | 'ADMIN' = 'MEMBER',
) {
  return http.post<{ invitationId: string; invitationToken: string }>(
    '/billing/seats/member-invite',
    { email, intendedRole },
  );
}

export function revokeSeatInvitation(http: HttpClient, invitationId: string) {
  return http.delete<{ invitationId: string; status: string }>(
    `/billing/seats/invitations/${invitationId}`,
  );
}

export function listSeatInvitations(http: HttpClient): Promise<
  ApiResponse<
    Array<{
      id: string;
      token: string;
      email: string;
      intendedRole: 'ADMIN' | 'MEMBER' | 'GUEST' | 'VIEWER';
      reservesSeat: boolean;
      createdAt: string;
      expiresAt: string | null;
    }>
  >
> {
  return http.get('/billing/seats/invitations');
}

export function createCheckoutSession(
  http: HttpClient,
  tier: string,
  billingCycle: 'monthly' | 'annual',
  confirmBillingOwnership?: boolean,
) {
  return http.post<{ url: string }>('/billing/checkout-session', {
    tier,
    billingCycle,
    ...(confirmBillingOwnership !== undefined && { confirmBillingOwnership }),
  });
}

export function createPortalSession(http: HttpClient, body?: CreatePortalSessionBody) {
  return http.post<{ url: string }>('/billing/portal-session', body ?? {});
}

export function cancelSubscription(http: HttpClient) {
  return http.post<{ endsAt: string }>('/billing/cancel');
}

export function resumeSubscription(http: HttpClient) {
  return http.post<{ resumed: true }>('/billing/resume');
}
